import { dynamicImport } from '@core/helpers'
import { ROUTES } from '@router'

// The redirection is done manually on the auth middleware because
// we don't want the staff to be redirected to the client login page
const commonRedirectionConfig = {
  auth: { required: true, redirection: { name: ROUTES.ADMIN_TOOLS.HOME } },
  staff: { required: true },
}

const routes = [
  /**
   * TaskAnalyzer
   */
  {
    name: ROUTES.ADMIN_TOOLS.TASK_ANALYZER,
    path: 'task_analyzer',
    component: () =>
      dynamicImport(import('@admin-tools/modules/task-analyzer')),
    meta: commonRedirectionConfig,
  },
  /**
   * Catalog
   */
  {
    name: ROUTES.ADMIN_TOOLS.CATALOG.BULK_ACTIONS,
    path: 'catalog/bulk_actions',
    component: () =>
      dynamicImport(import('@admin-tools/modules/catalog/pages/bulk-actions')),
  },
  {
    name: ROUTES.ADMIN_TOOLS.CATALOG.AKENEO.PRODUCTS,
    path: 'catalog/akeneo/products',
    component: () =>
      dynamicImport(
        import('@admin-tools/modules/catalog/pages/akeneo/products/search'),
      ),
  },
  {
    path: 'catalog/akeneo/products/:akeneoProductId',
    component: () =>
      dynamicImport(
        import('@admin-tools/modules/catalog/pages/akeneo/products/single'),
      ),
    redirect: { name: ROUTES.ADMIN_TOOLS.CATALOG.AKENEO.PRODUCT.INDEX },
    children: [
      {
        name: ROUTES.ADMIN_TOOLS.CATALOG.AKENEO.PRODUCT.INDEX,
        path: 'details',
        component: () =>
          dynamicImport(
            import(
              '@admin-tools/modules/catalog/pages/akeneo/products/single/children/ProductDetails'
            ),
          ),
      },
      {
        path: 'backboxes',
        name: ROUTES.ADMIN_TOOLS.CATALOG.AKENEO.PRODUCT.BACKBOXES,
        component: () =>
          dynamicImport(
            import(
              '@admin-tools/modules/catalog/pages/akeneo/products/single/children/Backboxes'
            ),
          ),
      },
      {
        path: 'listings',
        name: ROUTES.ADMIN_TOOLS.CATALOG.AKENEO.PRODUCT.LISTINGS,
        component: () =>
          dynamicImport(
            import(
              '@admin-tools/modules/catalog/pages/akeneo/products/single/children/Listings'
            ),
          ),
      },
    ],
  },
  {
    name: ROUTES.ADMIN_TOOLS.CATALOG.AKENEO.IMAGES.UPLOAD,
    path: 'catalog/akeneo/images/upload',
    component: () =>
      dynamicImport(
        import('@admin-tools/modules/catalog/pages/akeneo/images/upload'),
      ),
  },
  {
    name: ROUTES.ADMIN_TOOLS.CATALOG.BUYBACK.CATEGORIES.LIST,
    path: 'catalog/buyback/categories',
    component: () =>
      dynamicImport(
        import('@admin-tools/modules/catalog/pages/buyback/categories'),
      ),
  },
  {
    name: ROUTES.ADMIN_TOOLS.CATALOG.BUYBACK.MERCHANT.PRODUCT.LISTINGS,
    path: 'catalog/buyback/merchants/:merchantId/products/:productId',
    component: () =>
      dynamicImport(
        import(
          '@admin-tools/modules/catalog/pages/buyback/merchant/product/listings'
        ),
      ),
  },
  {
    name: ROUTES.ADMIN_TOOLS.CATALOG.BUYBACK.SEARCH,
    path: 'catalog/buyback/listings/search',
    component: () =>
      dynamicImport(
        import('@admin-tools/modules/catalog/pages/buyback/search'),
      ),
  },
  {
    name: ROUTES.ADMIN_TOOLS.CATALOG.PRODUCTS.VALIDATION.HISTORY,
    path: 'catalog/products_validation/history',
    component: () =>
      dynamicImport(
        import(
          '@admin-tools/modules/catalog/pages/products-validation/history'
        ),
      ),
  },
  {
    name: ROUTES.ADMIN_TOOLS.CATALOG.PRODUCTS.REMATCH.ITEM,
    path: 'catalog/products_validation/rematch/:productId',
    component: () =>
      dynamicImport(
        import(
          '@admin-tools/modules/catalog/pages/products-validation/rematch/search'
        ),
      ),
  },
  {
    name: ROUTES.ADMIN_TOOLS.CATALOG.CATEGORIES.RESTRICTED.LIST,
    path: 'catalog/categories/restricted',
    component: () =>
      dynamicImport(
        import('@admin-tools/modules/catalog/pages/categories/restricted'),
      ),
  },
  /**
   * Customers
   */
  {
    name: ROUTES.ADMIN_TOOLS.CUSTOMERS,
    path: 'customers',
    component: () => dynamicImport(import('@admin-tools/modules/customers')),
  },
  /**
   * Listing
   */
  {
    name: ROUTES.ADMIN_TOOLS.LISTINGS.SEARCH,
    path: 'listings',
    component: () =>
      dynamicImport(import('@admin-tools/modules/listings/pages/search')),
  },
  {
    path: 'listings/:listingId/lifecycle',
    component: () =>
      dynamicImport(import('@admin-tools/modules/listings/pages/lifecycle')),
    redirect: { name: ROUTES.ADMIN_TOOLS.LISTINGS.LIFECYCLE.INDEX },
    children: [
      {
        path: 'marketized-attributes',
        name: ROUTES.ADMIN_TOOLS.LISTINGS.LIFECYCLE.INDEX,
        component: () =>
          dynamicImport(
            import(
              '@admin-tools/modules/listings/pages/lifecycle/children/MarketizedAttributes'
            ),
          ),
      },
      {
        path: 'online-conditions',
        name: ROUTES.ADMIN_TOOLS.LISTINGS.LIFECYCLE.ONLINE_CONDITIONS,
        component: () =>
          dynamicImport(
            import(
              '@admin-tools/modules/listings/pages/lifecycle/children/OnlineConditions'
            ),
          ),
      },
      {
        path: 'history',
        name: ROUTES.ADMIN_TOOLS.LISTINGS.LIFECYCLE.HISTORY,
        component: () =>
          dynamicImport(
            import(
              '@admin-tools/modules/listings/pages/lifecycle/children/History'
            ),
          ),
      },
    ],
  },
  {
    name: ROUTES.ADMIN_TOOLS.LISTINGS.PRODUCT.UPDATE,
    path: 'listings/product/update/:productId',
    component: () =>
      dynamicImport(
        import('@admin-tools/modules/listings/pages/product/update'),
      ),
  },
  /** Sales Limitations */
  {
    name: ROUTES.ADMIN_TOOLS.SALES.LIMITATIONS,
    path: 'sales-limitations',
    component: () =>
      dynamicImport(
        import('@admin-tools/modules/sales/pages/sales-limitations'),
      ),
  },
  {
    name: ROUTES.ADMIN_TOOLS.SALES.RATINGS_REVIEWS,
    path: 'ratings-reviews',
    component: () =>
      dynamicImport(import('@admin-tools/modules/sales/pages/ratings-reviews')),
  },
  /** Sellers */
  {
    path: 'sellers/:sellerId',
    component: () =>
      dynamicImport(import('@admin-tools/modules/sellers/pages/single')),
    children: [
      {
        name: ROUTES.ADMIN_TOOLS.SELLERS.SINGLE.BUYBACK_LISTINGS,
        path: 'buyback-listings',
        component: () =>
          dynamicImport(
            import(
              '@admin-tools/modules/sellers/pages/single/children/BuybackListings'
            ),
          ),
      },
    ],
  },
]

export default () => {
  return [
    {
      path: '/bo_admin',
      component: () =>
        dynamicImport(import('./shared/components/layouts/Base')),
      meta: commonRedirectionConfig,
      children: routes,
    },
    /**
     * Legacy routes
     */
    {
      name: ROUTES.ADMIN_TOOLS.HOME,
      path: '/bo_merchant/admin_tools/dashboard',
      meta: { rollout: false },
    },
    {
      name: ROUTES.ADMIN_TOOLS.CATALOG.PRODUCTS.LIST,
      path: '/bo_merchant/admin_tools/choose_product_to_duplicate',
      meta: { rollout: false },
    },
    {
      name: ROUTES.ADMIN_TOOLS.CATALOG.PRODUCTS.REMATCH.LIST,
      path: '/bo_merchant/admin_tools/validate_products',
      meta: { rollout: false },
    },
    {
      name: ROUTES.ADMIN_TOOLS.SALES.ORDERS,
      path: '/bo_merchant/admin_tools/orders',
      meta: { rollout: false },
    },
    {
      name: ROUTES.ADMIN_TOOLS.SALES.TRANSACTIONS.HISTORY,
      path: '/bo_merchant/admin_tools/bank',
      meta: { rollout: false },
    },
  ]
}
